import { format, isAfter, isBefore, isEqual, isSameDay, parse } from "date-fns";
import { ChevronDown } from "lucide-react";
import {
	type ComponentProps,
	forwardRef,
	memo,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { ENABLE_PAST_DATE_RESCHEDULING_BYPASS } from "@/constants/appointment-rescheduling";
import {
	type DisabledVisit,
	useAppointmentDateTime,
} from "@/hooks/admin-portal/appointment/use-appointment-utils";
import {
	checkPastTimeSlot,
	generateTimeSlots,
} from "@/hooks/use-calendar-schedule";
import { hasVisitTime, toValidDate } from "@/lib/appointments/form";
import { QUICK_SLOT_ANCHORS } from "@/lib/therapist-scheduling/constants";
import { splitQuickTimes } from "@/lib/therapist-scheduling/quick-slots";
import { cn } from "@/lib/utils";

/**
 * Quick-pick anchors, shared with the calendar mode's therapist cards so both
 * schedule modes offer the same hours. The "grid" variant below renders them as
 * a fixed row; the "select" variant feeds them through `splitQuickTimes`.
 */
const UNIQUE_TIME_SLOTS: readonly string[] = QUICK_SLOT_ANCHORS;

export const TimeSlotButton = memo(function TimeSlotButton({
	time,
	isSelected,
	isDisabled = false,
	onSelect,
	buttonRef,
	label,
	ariaPressed,
}: {
	time: string;
	isSelected: boolean;
	isDisabled?: boolean;
	onSelect?: () => void;
	buttonRef?: React.Ref<HTMLButtonElement>;
	/**
	 * Overrides the rendered label. Opt-in: callers that already hold a
	 * formatted time string (e.g. the scheduling picker's `formatSlotTime`)
	 * pass it here instead of re-deriving it. Omitted by default, so existing
	 * callers keep the locale-aware label computed below.
	 */
	label?: string;
	/**
	 * Emits `aria-pressed` so the chip reads as a toggle to assistive tech.
	 * Opt-in — omitted by default, leaving existing callers' markup unchanged.
	 */
	ariaPressed?: boolean;
}) {
	const { locale, tzDate } = useDateContext();

	// Optimization: Memoize the formatted time string to avoid recalculation on every render.
	const displayTime = useMemo(() => {
		const slotDate = parse(time, "HH:mm", new Date(), {
			locale,
			in: tzDate,
		});

		// Use locale-based time format: AM/PM for English, 24-hour for Indonesian
		const timeFormat = locale.code === "id" ? "HH:mm" : "hh:mm a";

		return format(slotDate, timeFormat, {
			locale,
			in: tzDate,
		});
	}, [time, locale, tzDate]);

	return (
		<Button
			ref={buttonRef}
			type="button"
			variant={isSelected ? "default" : "primary-outline"}
			className={cn("w-full", !isSelected && "bg-sidebar shadow-inner")}
			disabled={isDisabled}
			aria-pressed={ariaPressed}
			onClick={(event) => {
				event.preventDefault();
				event.stopPropagation();

				if (onSelect && typeof onSelect === "function") {
					onSelect();
				}
			}}
		>
			{label ?? displayTime}
		</Button>
	);
});

export interface DateTimePickerProps extends ComponentProps<"div"> {
	value: Date | null;
	min?: Date | null;
	max?: Date | null;
	disabledVisits?: DisabledVisit[];
	onChangeValue: (...event: any[]) => void;
	callbackOnChange?: (newValue: Date | null) => void;
	isAllOfDay?: boolean;
	autoScroll?: boolean;
	allowPastTimes?: boolean;
	/**
	 * "grid" (default) renders the slot-button grid; "select" renders a compact
	 * time dropdown — used by the legacy "cari terapis" schedule mode to save
	 * space. Absent by default so existing callers (e.g. reschedule) keep the grid.
	 */
	timeVariant?: "grid" | "select";
}

const DateTimePicker = forwardRef<HTMLDivElement, DateTimePickerProps>(
	function DateTimePicker(
		{
			className,
			value,
			min = null,
			max = null,
			disabledVisits = [],
			isAllOfDay = false,
			autoScroll = false,
			allowPastTimes = false,
			timeVariant = "grid",
			onChangeValue,
			callbackOnChange,
		}: DateTimePickerProps,
		ref,
	) {
		const { locale, tzDate } = useDateContext();
		const { t } = useTranslation("appointments-form", {
			keyPrefix: "appt_schedule.fields",
		});
		const {
			appointmentDate,
			appointmentTime,
			appointmentDateCalendarProps,
			onSelectAppointmentDate,
			onSelectAppointmentTime,
		} = useAppointmentDateTime({
			sourceValue: { date: value || undefined, isAllOfDay },
			min,
			max,
			disabledVisits,
		});
		const calendarRef = useRef<HTMLDivElement>(null);
		const selectedButtonRef = useRef<HTMLButtonElement>(null);
		const [calendarHeight, setCalendarHeight] = useState<number>(0);
		const [timeGroupType, setTimeGroupType] = useState<"unique" | "basic">(
			"basic",
		);
		const [isMoreTimesOpen, setIsMoreTimesOpen] = useState(false);

		// Set up ref forwarding
		useImperativeHandle(ref, () => {
			if (!calendarRef.current) {
				// Create a dummy div as fallback - this should never happen
				// but satisfies TypeScript's type requirements
				return document.createElement("div");
			}
			return calendarRef.current;
		}, []);

		const handleDateSelect = useCallback(
			(date: Date | undefined) => {
				const newValue = onSelectAppointmentDate(date);
				// A day with no time yet stays in the picker until a time is chosen:
				// committing it would store midnight. All-of-day search is the exception,
				// it needs the bare day to list therapists and takes the time from a slot.
				if (newValue && !appointmentTime && !isAllOfDay) return;
				onChangeValue(newValue);
				callbackOnChange?.(newValue);
			},
			[
				onSelectAppointmentDate,
				onChangeValue,
				callbackOnChange,
				appointmentTime,
				isAllOfDay,
			],
		);
		const handleTimeSelect = useCallback(
			(time: string, type: "unique" | "basic") => {
				const date = onSelectAppointmentTime(time);
				if (date) {
					onChangeValue(date);
					callbackOnChange?.(date);
					setTimeGroupType(type);
				}
			},
			[onSelectAppointmentTime, onChangeValue, callbackOnChange],
		);
		const selectedTime = useMemo(() => {
			const validValue = toValidDate(value);
			return hasVisitTime(validValue) && validValue
				? format(validValue, "HH:mm")
				: null;
		}, [value]);
		// Midnight is the "no time yet" marker (see hasVisitTime), so it is not offered.
		const basicTimeSlots = useMemo(
			() => generateTimeSlots(30).filter((time) => time !== "00:00"),
			[],
		);
		// "select" variant only: anchor row inline, the rest behind the popover.
		const { quick: quickTimes, rest: restTimes } = useMemo(
			() => splitQuickTimes(basicTimeSlots, selectedTime),
			[basicTimeSlots, selectedTime],
		);
		const isTimeSlotDisabled = useCallback(
			(time: string): boolean => {
				if (isAllOfDay) {
					return true;
				}

				if (!appointmentDate) {
					return true;
				}

				// Check if this time slot is disabled due to another visit being scheduled
				// A time slot is disabled if it falls within the range [startTime, endTime) of any visit
				const isDisabledByVisit = disabledVisits.some((visit) => {
					const visitDate = new Date(visit.date);
					if (!isSameDay(appointmentDate, visitDate)) return false;

					const slotDate = parse(time, "HH:mm", appointmentDate);
					const visitStartDate = parse(
						visit.startTime,
						"HH:mm",
						appointmentDate,
					);
					const visitEndDate = parse(visit.endTime, "HH:mm", appointmentDate);

					return (
						(isEqual(slotDate, visitStartDate) ||
							isAfter(slotDate, visitStartDate)) &&
						isBefore(slotDate, visitEndDate)
					);
				});

				if (isDisabledByVisit) {
					return true;
				}

				// Only check past times if past date rescheduling bypass is disabled
				if (!ENABLE_PAST_DATE_RESCHEDULING_BYPASS && !allowPastTimes) {
					if (
						checkPastTimeSlot({
							time,
							date: appointmentDate,
							dateOpt: { locale, in: tzDate },
						})
					) {
						return true;
					}
				}

				return false;
			},
			[
				isAllOfDay,
				appointmentDate,
				locale,
				tzDate,
				disabledVisits,
				allowPastTimes,
			],
		);

		// * measure calendar height whenever it renders or resizes
		useEffect(() => {
			if (!calendarRef.current) return;
			const el = calendarRef.current;

			// Initial measurement
			setCalendarHeight(el.offsetHeight);

			// Watch for dynamic size changes
			const observer = new ResizeObserver(() => {
				setCalendarHeight(el.offsetHeight);
			});
			observer.observe(el);
			return () => observer.disconnect();
		}, []);

		// * auto-scroll selected time into center
		useEffect(() => {
			if (timeGroupType === "unique" || !autoScroll) return;

			if (selectedButtonRef?.current && value && calendarHeight) {
				selectedButtonRef.current.scrollIntoView({
					behavior: "smooth",
					block: "center",
				});
			}
		}, [value, autoScroll, calendarHeight, timeGroupType]);

		return (
			<div
				ref={calendarRef}
				className={cn("flex flex-col md:flex-row gap-2", className)}
			>
				<div className="flex flex-col gap-1.5">
					{timeVariant === "select" ? (
						<span className="font-medium text-sm">{t("appt_date.short")}</span>
					) : null}
					<Calendar
						{...appointmentDateCalendarProps}
						locale={locale}
						mode="single"
						captionLayout="dropdown"
						className="p-1 border rounded-md border-border bg-background"
						selected={
							(appointmentDate ?? value) != null
								? new Date((appointmentDate ?? value) as string | number | Date)
								: undefined
						}
						defaultMonth={value || undefined}
						onSelect={handleDateSelect}
					/>
				</div>

				{timeVariant === "select" ? (
					<div className="flex w-full flex-col gap-2">
						<span className="font-medium text-sm">{t("appt_time.label")}</span>

						{/* Quick-pick blocks for the most-common visit times. They wrap rather
						    than sit in a fixed 3-column grid, so each button takes its content
						    width and reflows in a narrow container. `[&>button]:w-auto` undoes
						    TimeSlotButton's w-full, which the grid variant below still needs.
						    A time picked from the popover is pinned in here by
						    `splitQuickTimes`, so the row always shows the current selection. */}
						<div className="flex flex-wrap gap-2 [&>button]:w-auto">
							{quickTimes.map((time) => (
								<TimeSlotButton
									key={`${time}-quick`}
									time={time}
									isSelected={selectedTime === time}
									isDisabled={isTimeSlotDisabled(time)}
									onSelect={() => handleTimeSelect(time, "unique")}
								/>
							))}
						</div>

						{/* Full 30-minute list for any other time. A popover rather than a
						    dropdown so the times stay clickable blocks, matching the calendar
						    mode's therapist cards. */}
						{restTimes.length > 0 && (
							<Popover onOpenChange={setIsMoreTimesOpen} open={isMoreTimesOpen}>
								<PopoverTrigger asChild>
									<Button
										className="w-fit gap-1.5 px-2 text-muted-foreground text-xs"
										disabled={!appointmentDate || isAllOfDay}
										size="sm"
										type="button"
										variant="ghost"
									>
										{t("appt_time.more", { count: restTimes.length })}
										<ChevronDown className="size-3.5 shrink-0" />
									</Button>
								</PopoverTrigger>

								<PopoverContent align="start" className="w-72 p-2">
									<p className="px-1 pb-2 font-medium text-muted-foreground text-xs">
										{t("appt_time.more_title")}
									</p>
									{/* Caps at ~5 rows; the full day scrolls inside the popover
									    rather than growing it past the viewport. */}
									<div className="grid max-h-56 grid-cols-3 gap-1.5 overflow-y-auto [&>button]:w-full">
										{restTimes.map((time) => (
											<TimeSlotButton
												key={`${time}-more`}
												isDisabled={isTimeSlotDisabled(time)}
												isSelected={selectedTime === time}
												onSelect={() => {
													handleTimeSelect(time, "basic");
													setIsMoreTimesOpen(false);
												}}
												time={time}
											/>
										))}
									</div>
								</PopoverContent>
							</Popover>
						)}
					</div>
				) : (
					<ScrollArea
						style={{ height: calendarHeight }}
						className="w-full h-64 gap-4 py-1 md:w-auto"
					>
						<div className="grid grid-cols-1 gap-2 md:grid-cols-3">
							{UNIQUE_TIME_SLOTS.map((time) => (
								<TimeSlotButton
									key={`${time}-unique`}
									time={time}
									isSelected={selectedTime === time}
									isDisabled={isTimeSlotDisabled(time)}
									onSelect={() => handleTimeSelect(time, "unique")}
								/>
							))}
						</div>

						<Separator className="my-4" />

						<div className="grid grid-cols-1 gap-2 md:grid-cols-3">
							{basicTimeSlots.map((time) => (
								<TimeSlotButton
									key={time}
									buttonRef={selectedTime === time ? selectedButtonRef : null}
									time={time}
									isSelected={selectedTime === time}
									isDisabled={isTimeSlotDisabled(time)}
									onSelect={() => handleTimeSelect(time, "basic")}
								/>
							))}
						</div>
					</ScrollArea>
				)}
			</div>
		);
	},
);

DateTimePicker.displayName = "DateTimePicker";

export default DateTimePicker;
